import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, Plus, Search } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { useMemberships, switchActiveCompany } from '../hooks/useMemberships'
import { useAchievement } from '../contexts/AchievementContext'
import CreateCompanyModal from './CreateCompanyModal'

function companyInitials(name) {
    if (!name) return '?'
    const clean = name.replace(/[^A-Za-zĂÂÎȘȚăâîșț0-9 ]/g, '').trim()
    const parts = clean.split(/\s+/)
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
    return clean.slice(0, 2).toUpperCase()
}

function flagGradient(name) {
    const hash = (name || 'XX').split('').reduce((a, c) => a + c.charCodeAt(0), 0)
    const hue = hash % 360
    return `linear-gradient(135deg, oklch(0.55 0.20 ${hue}), oklch(0.55 0.15 ${(hue + 120) % 360}))`
}

export default function CompanySwitcher({ activeCompanyId, onCompanyChanged }) {
    const { t } = useTranslation()
    const { user, profile, isAdmin, isSuperAdmin, refreshProfile } = useAuth()
    const { memberships, refetch } = useMemberships(user?.id)
    const { showAchievement } = useAchievement()
    const [open, setOpen] = useState(false)
    const [showCreateModal, setShowCreateModal] = useState(false)
    const [switching, setSwitching] = useState(false)
    const [coords, setCoords] = useState({ top: 0, left: 0, width: 280 })
    const containerRef = useRef(null)
    const dropdownRef = useRef(null)

    useEffect(() => {
        if (!open) return
        const onClickOutside = (e) => {
            if (dropdownRef.current && dropdownRef.current.contains(e.target)) return
            if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false)
        }
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
        document.addEventListener('mousedown', onClickOutside)
        document.addEventListener('keydown', onKey)
        return () => {
            document.removeEventListener('mousedown', onClickOutside)
            document.removeEventListener('keydown', onKey)
        }
    }, [open])

    useLayoutEffect(() => {
        if (!open || !containerRef.current) return
        const rect = containerRef.current.getBoundingClientRect()
        const viewportPadding = 12
        const width = Math.max(286, Math.min(360, rect.width - 24))
        const left = Math.min(Math.max(rect.left + 12, viewportPadding), window.innerWidth - width - viewportPadding)
        const top = Math.min(rect.bottom + 6, window.innerHeight - 16)
        setCoords({ top, left, width })
    }, [open, memberships.length])

    const activeId = activeCompanyId || profile?.active_company_id || profile?.company_id
    const activeMembership = memberships.find((m) => m.company_id === activeId) || null
    const activeName = activeMembership?.companies?.name || profile?.company_name || t('company.yourCompany')
    const activeRole = activeMembership?.role || (isAdmin ? 'admin' : 'employee')

    const handleSwitch = async (companyId) => {
        if (companyId === activeId) {
            setOpen(false)
            return
        }
        setSwitching(true)
        const res = await switchActiveCompany(companyId)
        setSwitching(false)
        if (!res.success) {
            showAchievement({ type: 'error', message: res.error })
            return
        }
        await refreshProfile?.()
        setOpen(false)
        onCompanyChanged?.(companyId)
        showAchievement({ type: 'achievement', message: t('company.switched') })
    }

    const handleCreated = async ({ companyId }) => {
        setShowCreateModal(false)
        await refetch()
        await refreshProfile?.()
        onCompanyChanged?.(companyId)
        showAchievement({ type: 'achievement', message: t('company.createdSuccess') })
    }

    if (!profile?.company_id && memberships.length === 0) return null

    return (
        <div className="company-switcher-wrap" ref={containerRef}>
            <button
                type="button"
                className={`sidebar-company-switcher ${open ? 'is-open' : ''}`.trim()}
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                aria-haspopup="listbox"
                title={t('company.switchCompany')}
            >
                <div className="sidebar-company-flag" style={{ background: flagGradient(activeName) }}>
                    {companyInitials(activeName)}
                </div>
                <div className="sidebar-company-info">
                    <div className="sidebar-company-name">{activeName}</div>
                    <div className="sidebar-company-meta">
                        {activeRole === 'admin' ? t('company.roleAdmin') : activeRole === 'manager' ? t('company.roleManager') : t('company.roleEmployee')}
                        {memberships.length > 1 ? ` · ${t('company.companiesCount', { count: memberships.length })}` : ''}
                    </div>
                </div>
                <svg
                    className="sidebar-company-chevron"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}
                >
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>

            {open && createPortal(
                <div
                    className="company-switcher-dropdown"
                    role="listbox"
                    ref={dropdownRef}
                    style={{ top: coords.top, left: coords.left, width: coords.width }}
                >
                    <div className="csd-section-label">{t('company.yourCompaniesWithCount', { count: memberships.length })}</div>
                    {memberships.map((m) => {
                        const isActiveOne = m.company_id === activeId
                        return (
                            <button
                                key={m.company_id}
                                type="button"
                                className={`csd-item ${isActiveOne ? 'active' : ''}`.trim()}
                                onClick={() => handleSwitch(m.company_id)}
                                disabled={switching}
                                role="option"
                                aria-selected={isActiveOne}
                            >
                                <div className="csd-flag" style={{ background: flagGradient(m.companies?.name) }}>
                                    {companyInitials(m.companies?.name)}
                                </div>
                                <div className="csd-body">
                                    <div className="csd-name">{m.companies?.name || '—'}</div>
                                    <div className="csd-sub">
                                        <span className={`csd-role csd-role-${m.role}`}>{m.role}</span>
                                        <span className="csd-sub-loc">
                                            {m.companies?.location || t('company.noLocation')}
                                            {m.companies?.join_code ? ` · ${m.companies.join_code}` : ''}
                                        </span>
                                    </div>
                                </div>
                                {isActiveOne && <Check size={14} className="csd-check" />}
                            </button>
                        )
                    })}

                    {/* Platform actions — super-admin only. A regular
                        employee/admin must NOT be able to create or join
                        companies (invitation-only org model). */}
                    {isSuperAdmin && (
                        <>
                            <div className="csd-divider" />
                            <button type="button" className="csd-action csd-action-primary" onClick={() => { setShowCreateModal(true); setOpen(false) }}>
                                <Plus size={16} />
                                {t('company.createNew')}
                            </button>
                            <button type="button" className="csd-action" disabled title={t('company.comingSoon')}>
                                <Search size={16} />
                                {t('company.joinWithCode')}
                            </button>
                        </>
                    )}
                </div>,
                document.body,
            )}

            {showCreateModal && (
                <CreateCompanyModal
                    onClose={() => setShowCreateModal(false)}
                    onCreated={handleCreated}
                />
            )}
        </div>
    )
}
